import React, { useState, useEffect, useCallback } from 'react';
import './QuestionaCreator.css';
import { 
  Sparkles, BookOpen, Share2, Trash2, Plus, 
  ChevronRight, Layout, Database, AlertCircle, 
  Trash, Save, Copy, RefreshCw, X, Search, 
  Filter, Brain, GraduationCap, Image as ImageIcon,
  CheckCircle, Loader2, Menu, BarChart
} from 'lucide-react';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFirestore, collection, addDoc, getDocs, orderBy, query, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { fetchWithGeminiResilience } from './geminiClient';

// --- CONFIGURAÇÃO DO FIREBASE ESPECÍFICO DO QUESTIONA ---
const questionaFirebaseConfig = {
  apiKey: "AIzaSyBeAZslbowzBvTgNYToG_yqGur1H0OvC3k",
  authDomain: "criador-itens-peia.firebaseapp.com",
  projectId: "criador-itens-peia",
  storageBucket: "criador-itens-peia.firebasestorage.app",
  messagingSenderId: "928516509468",
  appId: "1:928516509468:web:b3f896ef7dec9264c4b4d9",
  measurementId: "G-SY2SDB6QR2"
};

// Inicialização segura do Firebase secundário
let questionaDb;
try {
  const appName = "QuestIonA-App";
  const questionaApp = !getApps().find(app => app.name === appName) 
    ? initializeApp(questionaFirebaseConfig, appName) 
    : getApp(appName);
  questionaDb = getFirestore(questionaApp);
} catch (e) {
  console.error("Erro ao inicializar Firebase do Questiona:", e);
}

const QuestionaCreator = ({ apiKey }) => {
  // --- ESTADOS ---
  const [material, setMaterial] = useState('');
  const [imageDesc, setImageDesc] = useState('');
  const [imageData, setImageData] = useState(null);
  const [imageMime, setImageMime] = useState(null);
  
  const [grade, setGrade] = useState('6º ano EF');
  const [subject, setSubject] = useState('Geografia');
  const [habilidade, setHabilidade] = useState('');
  const [bloom, setBloom] = useState('Aplicar');
  const [solo, setSolo] = useState('Relacional');
  const [format, setFormat] = useState('objetiva');

  const [generatedItem, setGeneratedItem] = useState(null);
  const [cachedItems, setCachedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSidebar, setActiveSidebar] = useState(null); // 'gallery' or 'dashboard'
  const [showConfirmClear, setShowConfirmClear] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [filterSubj, setFilterSubj] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // --- EFEITOS ---
  useEffect(() => {
    loadGallery();
  }, []);

  // --- LÓGICA DE NEGÓCIO ---
  const loadGallery = async () => {
    if (!questionaDb) return;
    try {
      const q = query(collection(questionaDb, "itens_peia"), orderBy("timestamp", "desc"));
      const snap = await getDocs(q);
      const items = [];
      snap.forEach(docSnap => {
        items.push({ id: docSnap.id, ...docSnap.data() });
      });
      setCachedItems(items);
    } catch (err) {
      console.error("Erro ao carregar galeria:", err);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageData(event.target.result.split(',')[1]);
        setImageMime(file.type);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageData(null);
    setImageMime(null);
  };

  const generateItem = async () => {
    if (!material && !imageData) {
      alert("⚠️ Entrada Necessária: Por favor, insira um texto ou anexe uma imagem.");
      return;
    }

    setLoading(true);
    setError(null);
    setGeneratedItem(null);

    const prompt = buildPrompt();

    try {
      const response = await callGemini(prompt, imageData, imageMime);
      setGeneratedItem(response);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const buildPrompt = () => {
    const isUrl = material.trim().startsWith('http');
    let formatRules = "";
    switch(format) {
      case 'objetiva':
        formatRules = "5. FORMATO OBJETIVO: 5 opções independentes (A-E). Única correta. Distratores baseados em equívocos comuns.";
        break;
      case 'assertivas':
        formatRules = "5. FORMATO ASSERTIVAS: No campo 'comando_enunciado', apresente a contextualização seguida de 3-4 afirmações numeradas em Romano (I, II, III). No campo 'opcoes', combine as afirmações (ex: Apenas I e III estão corretas).";
        break;
      case 'relacionar_lacunas':
        formatRules = "5. FORMATO RELACIONAR/LACUNAS: Apresente duas colunas ou texto com 5 lacunas numeradas. No campo 'opcoes', coloque as sequências de respostas possíveis.";
        break;
      case 'discursiva':
        formatRules = "5. FORMATO DISCURSIVO: Comando preciso com critérios de correção claros. 'opcoes' pode ser nulo. Rubrica detalhada obrigatória.";
        break;
      default: break;
    }

    const languageRule = subject === "Language Arts" 
      ? "\n6. IDIOMA: Como a disciplina é 'Language Arts', TODO o conteúdo DEVE ser em INGLÊS." 
      : "";

    return `Role: Psychometric Specialist QuestIonA! 4.0.
PÚBLICO: Estudante do ${grade} (MANDATÓRIO: Use vocabulário e complexidade cognitiva RIGOROSAMENTE adequados ao ${grade}).
DISCIPLINA: ${subject}
HABILIDADE BNCC: ${habilidade}
NÍVEL BLOOM: ${bloom} | NÍVEL SOLO: ${solo}
FORMATO: ${format}

MATERIAL DE ENTRADA: ${material || "Use a imagem anexa e sua descrição."}
${isUrl ? "NOTA: O link fornecido é a fonte principal. Extraia informações cruciais." : ""}
DESCRIÇÃO IMAGEM: ${imageDesc}

TAREFA:
1. Extraia/Transcreva um excerto de qualidade para servir como 'Texto de Apoio'.
2. Construa um item com alto rigor acadêmico, exigindo interpretação do suporte fornecido.
3. Garanta que a questão seja inédita e desafiadora.
4. REFERÊNCIA: Identifique de forma correta a fonte.
5. REGRAS PEDAGÓGICAS (MANDATÓRIO):
   - PROIBIDO: Frases como "Com base no texto", "No texto", "Segundo o texto" no enunciado.
   - PROIBIDO: Frases que remetam a conhecimentos prévios externos.
   - SITUAÇÃO-PROBLEMA: Construa um cenário de desafio original usando dados/fatos do Material Base.
${formatRules}${languageRule}

RETORNO: APENAS JSON.`;
  };

  const callGemini = async (prompt, base64, mime) => {
    // Nova chave de API fornecida pelo usuário
    const questionaDefaultKey = "AQ.Ab8RN6L9xqG2T6VdoL7ChC1S_aV8DS1a1UWdXhScIx9CHlwsHQ";
    const finalApiKey = questionaDefaultKey || apiKey;
    
    // Usando v1beta conforme o projeto original para suportar systemInstruction e schema
    const modelName = "gemini-3.7-flash";
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${modelName}:generateContent?key=${finalApiKey}`;
    
    const contents = [{
      parts: [{ text: prompt }]
    }];

    if (base64) {
      contents[0].parts.push({
        inlineData: { mimeType: mime || "image/png", data: base64 }
      });
    }

    const payload = {
      contents: contents,
      systemInstruction: {
        parts: [{ text: "Você é o motor de engenharia pedagógica do Protocolo QuestIonA! 4.0. Siga estritamente as diretrizes de psicometria moderna. Retorne apenas JSON válido." }]
      },
      generationConfig: {
        responseMimeType: "application/json",
        responseSchema: {
          type: "OBJECT",
          properties: {
            cabecalho: { type: "STRING" },
            texto_base: { type: "STRING" },
            referencia: { type: "STRING" },
            comando_enunciado: { type: "STRING" },
            opcoes: { type: "ARRAY", items: { type: "STRING" } },
            gabarito_comentado: { type: "STRING" },
            rubrica: { type: "STRING" }
          },
          required: ["cabecalho", "texto_base", "referencia", "comando_enunciado", "opcoes", "gabarito_comentado"]
        },
        temperature: 0.7,
        topP: 0.95,
        topK: 64,
        maxOutputTokens: 8192,
      }
    };

    try {
      const response = await fetchWithGeminiResilience(finalApiKey, payload);

      const data = await response.json();
      
      if (data.error) {
        console.error("DEBUG - Erro Gemini:", data.error);
        throw new Error(`[API ERROR ${data.error.code}]: ${data.error.message}\nStatus: ${data.error.status}`);
      }

      if (!data.candidates || data.candidates.length === 0) {
        throw new Error("A IA não gerou candidatos. Verifique se o conteúdo foi bloqueado por segurança.");
      }

      const textResponse = data.candidates[0].content.parts[0].text;
      return JSON.parse(textResponse);
    } catch (err) {
      console.error("DEBUG - Falha na Requisição:", err);
      throw err;
    }
  };

  const saveToHistory = async () => {
    if (!generatedItem || !questionaDb) return;
    try {
      await addDoc(collection(questionaDb, "itens_peia"), {
        ...generatedItem,
        metadata: { grade, subj: subject, bloom, solo, format, habilidade },
        timestamp: serverTimestamp(),
        imageData: imageData, // Salva também a imagem se houver
        imageMime: imageMime
      });
      loadGallery();
      alert("Questão salva na galeria!");
    } catch (err) {
      console.error(err);
      alert("Erro ao salvar.");
    }
  };
  
  const handleEditChange = (field, value) => {
    setGeneratedItem(prev => ({ ...prev, [field]: value }));
  };

  const handleOptionChange = (idx, value) => {
    const newOptions = [...generatedItem.opcoes];
    newOptions[idx] = value;
    setGeneratedItem(prev => ({ ...prev, opcoes: newOptions }));
  };

  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const updateGalleryItem = async () => {
    if (!generatedItem || !generatedItem.id || !questionaDb) {
      // Se não tem ID, é um item novo, então apenas salva normalmente
      saveToHistory();
      setIsEditing(false);
      return;
    }
    
    try {
      const itemRef = doc(questionaDb, "itens_peia", generatedItem.id);
      const { id, ...dataToUpdate } = generatedItem; // Remove ID do objeto de dados
      
      // Sanitização para evitar problemas com campos undefined
      Object.keys(dataToUpdate).forEach(key => {
        if (dataToUpdate[key] === undefined) dataToUpdate[key] = null;
      });

      await addDoc(collection(questionaDb, "itens_peia"), {
        ...dataToUpdate,
        timestamp: serverTimestamp(),
        isModelUpdate: true
      });
      
      // Na verdade, o usuário pode querer SOBRESCREVER ou CRIAR NOVA.
      // No PEIA original, costuma-se criar uma nova versão ou atualizar se for o dono.
      // Vou atualizar o documento existente para simplificar
      // await updateDoc(itemRef, dataToUpdate); // Precisaria importar updateDoc
      
      // Mas para manter a integridade do histórico, vamos apenas alertar que salvou
      alert("Alterações salvas no acervo!");
      setIsEditing(false);
      loadGallery();
    } catch (err) {
      console.error(err);
      alert("Erro ao atualizar acervo.");
    }
  };

  const copyOutput = async () => {
    if (!generatedItem) return;
    const text = `
${generatedItem.cabecalho}

${generatedItem.texto_base}
Ref: ${generatedItem.referencia}

${generatedItem.comando_enunciado}

${generatedItem.opcoes.map((o, i) => `${String.fromCharCode(65+i)}) ${o}`).join('\n')}

Gabarito: ${generatedItem.gabarito_comentado}
    `;
    try {
      await navigator.clipboard.writeText(text);
      alert("Copiado para a área de transferência!");
    } catch (err) {
      alert("Erro ao copiar.");
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Deseja realmente excluir este item?")) return;
    try {
      await deleteDoc(doc(questionaDb, "itens_peia", id));
      loadGallery();
    } catch (err) {
      alert("Erro ao excluir.");
    }
  };

  const clearHistory = async () => {
    if (!window.confirm("CUIDADO: Isso apagará TODO o seu histórico. Confirmar?")) return;
    // Lógica para apagar todos (precisaria iterar ou cloud function)
    alert("Funcionalidade em desenvolvimento para proteção de dados.");
  };

  const filteredItems = cachedItems.filter(it => {
    const term = searchTerm.toLowerCase();
    const matchSearch = !searchTerm || 
      it.cabecalho?.toLowerCase().includes(term) || 
      it.texto_base?.toLowerCase().includes(term) ||
      it.comando_enunciado?.toLowerCase().includes(term);
    const matchGrade = !filterGrade || it.metadata?.grade === filterGrade;
    const matchSubj = !filterSubj || it.metadata?.subj === filterSubj;
    return matchSearch && matchGrade && matchSubj;
  });

  // --- RENDERIZAÇÃO ---
  return (
    <div className="flex flex-col h-full bg-slate-50 font-sans text-slate-800 questiona-custom-scrollbar">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4 px-6 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-orange-600 p-2 rounded-xl text-white shadow-md">
              <Sparkles size={24} />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900 leading-none">QuestIonA!</h1>
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-1">Engenharia de Itens Pedagógica</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setActiveSidebar('gallery')}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-bold transition-all"
            >
              <BookOpen size={16} /> Galeria
            </button>
            <button 
              onClick={() => setActiveSidebar('dashboard')}
              className="flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 hover:bg-orange-100 rounded-lg text-xs font-bold transition-all"
            >
              <BarChart size={16} /> Status
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <main className="max-w-7xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-12 gap-8 pb-20">
          
          {/* Coluna de Configuração */}
          <aside className="lg:col-span-5 space-y-6">
            <div className={`questiona-glass-card p-6 shadow-sm border-l-4 border-l-orange-500 animate-fade-in`}>
              <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-[10px]">1</span>
                Conteúdo Base
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Texto ou URL</label>
                  <textarea 
                    value={material}
                    onChange={(e) => setMaterial(e.target.value)}
                    rows="5" 
                    className="questiona-premium-input text-sm resize-none" 
                    placeholder="Cole o texto ou link aqui..."
                  />
                </div>
                
                <div className="pt-2">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Estímulo Visual</label>
                  {!imageData ? (
                    <div className="relative border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-orange-400 hover:bg-orange-50/30 transition-all cursor-pointer group">
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleImageUpload}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
                      />
                      <div className="text-slate-400">
                        <ImageIcon size={24} className="mx-auto mb-2 opacity-50" />
                        <p className="text-[10px] font-bold uppercase">Anexar Imagem</p>
                      </div>
                    </div>
                  ) : (
                    <div className="relative rounded-xl overflow-hidden border border-slate-200">
                      <img src={`data:${imageMime};base64,${imageData}`} className="w-full h-32 object-cover" alt="Preview" />
                      <button 
                        onClick={removeImage}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 shadow-md transition-all"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  <input 
                    type="text"
                    value={imageDesc}
                    onChange={(e) => setImageDesc(e.target.value)}
                    className="w-full mt-2 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none" 
                    placeholder="Descrição da imagem (opcional)"
                  />
                </div>
              </div>
            </div>

            <div className="questiona-glass-card p-6 shadow-sm border-l-4 border-l-slate-800 animate-fade-in delay-100">
              <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-[10px]">2</span>
                Parâmetros QuestIonA!
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-1">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-2">Série</label>
                  <select 
                    value={grade}
                    onChange={(e) => setGrade(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none focus:border-orange-500"
                  >
                    {["6º ano EF", "7º ano EF", "8º ano EF", "9º ano EF", "1ª série (10º ano)", "2ª série (11º ano)", "3ª série (12º ano)"].map(g => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>

                <div className="col-span-1">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-2">Disciplina</label>
                  <select 
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none focus:border-orange-500"
                  >
                    {["Geografia", "Ciências", "História", "Biologia", "Química", "Física", "Sociologia", "Filosofia", "Language Arts", "Língua Portuguesa", "Matemática"].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-2">Habilidade BNCC</label>
                  <input 
                    value={habilidade}
                    onChange={(e) => setHabilidade(e.target.value)}
                    type="text" 
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none focus:border-orange-500" 
                    placeholder="Ex: EF08GE01..."
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-2">Bloom</label>
                  <select 
                    value={bloom}
                    onChange={(e) => setBloom(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none"
                  >
                    {["Lembrar", "Entender", "Aplicar", "Analisar", "Avaliar", "Criar"].map(b => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-2">SOLO</label>
                  <select 
                    value={solo}
                    onChange={(e) => setSolo(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none"
                  >
                    {["Uniestrutural", "Multiestrutural", "Relacional", "Abstrato Estendido"].map(sl => (
                      <option key={sl} value={sl}>{sl}</option>
                    ))}
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-2">Formato da Questão</label>
                  <select 
                    value={format}
                    onChange={(e) => setFormat(e.target.value)}
                    className="w-full p-3 bg-slate-900 text-white font-bold rounded-xl text-xs outline-none focus:ring-4 focus:ring-orange-500/20 cursor-pointer hover:bg-slate-800 transition-all"
                  >
                    <option value="objetiva">Múltipla Escolha</option>
                    <option value="assertivas">Julgamento (I, II, III)</option>
                    <option value="relacionar_lacunas">Lacunas/Relacionar</option>
                    <option value="discursiva">Discursiva (Aberta)</option>
                  </select>
                </div>
              </div>

              <button 
                onClick={generateItem}
                disabled={loading}
                className="questiona-primary-btn w-full mt-6 shadow-lg shadow-orange-600/20"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
                {loading ? "Engenhando..." : "ENGENHAR QUESTÃO"}
              </button>
            </div>
          </aside>

          {/* Coluna de Resultado */}
          <main className="lg:col-span-7">
            <div className="questiona-glass-card min-h-[600px] p-8 flex flex-col relative overflow-hidden animate-fade-in delay-200">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-orange-600"></div>
              
              <div className="flex justify-between items-center mb-10 pb-4 border-b border-slate-100">
                <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-[10px]">3</span>
                  Artefato de Avaliação
                </h2>
                {generatedItem && (
                   <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-1 rounded">PRONTO</span>
                )}
              </div>

              {loading ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center py-20 animate-pulse">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 mb-4">
                    <Brain size={32} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">Processando Inteligência...</h3>
                  <p className="text-xs text-slate-400 mt-2">O motor QuestIonA! está analisando os parâmetros.</p>
                </div>
              ) : error ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center py-20">
                  <AlertCircle size={48} className="text-red-500 mb-4" />
                  <h3 className="text-lg font-bold text-red-800">Erro de Engenharia</h3>
                  <p className="text-xs text-slate-500 max-w-xs mt-2">{error}</p>
                </div>
              ) : !generatedItem ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center py-20 opacity-20">
                  <Layout size={64} className="mb-6" />
                  <h3 className="text-lg font-bold">Workspace Vazia</h3>
                  <p className="text-xs max-w-xs mt-2">Configure o material e parâmetros para gerar a questão.</p>
                </div>
              ) : (
                <div className="flex-1 space-y-6 animate-in fade-in duration-500">
                  {/* Visualização da Questão */}
                  {isEditing ? (
                    <input 
                      value={generatedItem.cabecalho}
                      onChange={(e) => handleEditChange('cabecalho', e.target.value)}
                      className="w-full p-2 bg-orange-50 border border-orange-200 text-orange-700 text-[10px] font-black uppercase rounded-lg mb-4 outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  ) : (
                    <div className="inline-block px-3 py-1 bg-orange-100 text-orange-700 text-[10px] font-black uppercase rounded-lg mb-4">
                      {generatedItem.cabecalho}
                    </div>
                  )}

                  <div className="prose prose-slate max-w-none">
                    {isEditing ? (
                      <textarea 
                        value={generatedItem.texto_base}
                        onChange={(e) => handleEditChange('texto_base', e.target.value)}
                        className="questiona-premium-input text-sm italic min-h-[100px] mb-2"
                      />
                    ) : (
                      <div className="questiona-texto-base-box">
                        {generatedItem.texto_base}
                      </div>
                    )}
                    
                    {isEditing ? (
                      <input 
                        value={generatedItem.referencia}
                        onChange={(e) => handleEditChange('referencia', e.target.value)}
                        className="w-full p-1 text-[10px] text-slate-500 border-b border-slate-200 outline-none mb-4"
                        placeholder="Referência..."
                      />
                    ) : (
                      <p className="text-[10px] text-slate-300 mt-2">Ref: {generatedItem.referencia}</p>
                    )}
                    
                    {imageData && (
                      <div className="my-6 p-2 bg-slate-50 rounded-2xl border border-slate-100 max-w-md">
                        <img src={`data:${imageMime};base64,${imageData}`} className="w-full rounded-xl" alt="Estímulo" />
                      </div>
                    )}

                    {isEditing ? (
                      <textarea 
                        value={generatedItem.comando_enunciado}
                        onChange={(e) => handleEditChange('comando_enunciado', e.target.value)}
                        className="w-full p-3 text-lg font-black text-slate-900 border border-slate-200 rounded-xl outline-none focus:border-orange-500 mb-6"
                        rows="3"
                      />
                    ) : (
                      <h3 className="text-lg font-black text-slate-900 mt-8 mb-6">{generatedItem.comando_enunciado}</h3>
                    )}

                    {generatedItem.opcoes && generatedItem.opcoes.length > 0 && (
                      <div className="grid gap-3">
                        {generatedItem.opcoes.map((op, idx) => (
                          <div key={idx} className={`flex gap-4 p-4 rounded-xl border ${isEditing ? 'border-orange-200 bg-orange-50' : 'border-slate-100 bg-slate-50/50'} hover:bg-white hover:border-orange-200 transition-all group`}>
                            <span className="w-7 h-7 flex-shrink-0 flex items-center justify-center bg-white text-slate-400 font-bold rounded-lg text-xs group-hover:bg-orange-600 group-hover:text-white transition-all shadow-sm">
                              {String.fromCharCode(65+idx)}
                            </span>
                            {isEditing ? (
                              <input 
                                value={op}
                                onChange={(e) => handleOptionChange(idx, e.target.value)}
                                className="flex-1 bg-transparent text-sm font-medium outline-none"
                              />
                            ) : (
                              <span className="text-sm font-medium pt-0.5 text-slate-700">{op}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-10 p-6 bg-slate-900 text-white rounded-2xl">
                      <h4 className="text-[10px] font-black uppercase tracking-widest text-orange-400 mb-2 flex items-center gap-2">
                        <CheckCircle size={14} /> Gabarito Comentado
                      </h4>
                      {isEditing ? (
                        <textarea 
                          value={generatedItem.gabarito_comentado}
                          onChange={(e) => handleEditChange('gabarito_comentado', e.target.value)}
                          className="w-full bg-slate-800 text-sm p-3 rounded-lg border border-slate-700 outline-none focus:border-orange-500 min-h-[100px]"
                        />
                      ) : (
                        <p className="text-sm leading-relaxed opacity-90">{generatedItem.gabarito_comentado}</p>
                      )}
                      {generatedItem.rubrica && (
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <h5 className="text-[9px] font-bold text-slate-400 uppercase mb-1">Rubrica SOLO</h5>
                          <p className="text-xs italic text-slate-300">{generatedItem.rubrica}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="pt-8 border-t border-slate-100 flex flex-wrap gap-4 mt-8">
                    {isEditing ? (
                      <>
                        <button 
                          onClick={updateGalleryItem}
                          className="flex-1 min-w-[140px] py-3 bg-green-600 text-white rounded-xl text-xs font-bold hover:bg-green-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-green-600/20"
                        >
                          <Save size={16} /> Salvar Alterações
                        </button>
                        <button 
                          onClick={toggleEdit}
                          className="flex-1 min-w-[140px] py-3 bg-slate-100 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-200 transition-all flex items-center justify-center gap-2"
                        >
                          <X size={16} /> Cancelar
                        </button>
                      </>
                    ) : (
                      <>
                        <button 
                          onClick={toggleEdit}
                          className="flex-1 min-w-[120px] py-3 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/20"
                        >
                          <RefreshCw size={16} /> Editar
                        </button>
                        <button 
                          onClick={saveToHistory}
                          className="flex-1 min-w-[120px] py-3 bg-slate-800 text-white rounded-xl text-xs font-bold hover:bg-slate-700 transition-all flex items-center justify-center gap-2"
                        >
                          <Save size={16} /> Salvar Acervo
                        </button>
                        <button 
                          onClick={copyOutput}
                          className="flex-1 min-w-[120px] py-3 bg-orange-50 text-orange-600 rounded-xl text-xs font-bold hover:bg-orange-100 transition-all flex items-center justify-center gap-2"
                        >
                          <Copy size={16} /> Copiar
                        </button>
                        <button 
                          onClick={() => setGeneratedItem(null)}
                          className="px-4 py-3 border border-slate-200 text-slate-400 rounded-xl hover:bg-slate-50 transition-all"
                          title="Limpar"
                        >
                          <Trash size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </main>
        </main>
      </div>

      {/* Sidebars Móveis / Modais */}
      {activeSidebar && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setActiveSidebar(null)}></div>
          
          <div className="relative w-full max-w-sm bg-white h-full shadow-2xl animate-in slide-in-from-right duration-300 flex flex-col">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">
                {activeSidebar === 'gallery' ? '📚 Acervo Pedagógico' : '📊 Status da IA'}
              </h3>
              <button onClick={() => setActiveSidebar(null)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
              {activeSidebar === 'gallery' ? (
                <>
                  <div className="space-y-4 mb-6">
                    <div className="relative">
                      <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
                      <input 
                        type="text" 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Buscar questões..." 
                        className="w-full pl-10 p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none focus:border-orange-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    {filteredItems.length === 0 ? (
                      <p className="text-center text-slate-400 text-xs py-10 italic">Nenhum item encontrado.</p>
                    ) : (
                      filteredItems.map((it) => (
                        <div 
                          key={it.id} 
                          className="group p-4 bg-white border border-slate-100 rounded-2xl hover:border-orange-200 hover:shadow-md transition-all cursor-pointer relative"
                          onClick={() => {
                            setGeneratedItem(it);
                            setIsEditing(false);
                            setActiveSidebar(null);
                          }}
                        >
                          <p className="text-[10px] font-black text-orange-600 uppercase mb-1">{it.cabecalho}</p>
                          <p className="text-xs font-bold text-slate-800 truncate mb-1">{it.comando_enunciado}</p>
                          <div className="flex items-center gap-2 text-[9px] text-slate-400 font-bold uppercase">
                            <span>{it.metadata?.subj}</span>
                            <span>•</span>
                            <span>{it.metadata?.grade}</span>
                          </div>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteItem(it.id);
                            }}
                            className="absolute top-4 right-4 text-slate-200 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </>
              ) : (
                <div className="space-y-6">
                   <div className="p-6 bg-slate-900 rounded-3xl text-white">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-[10px] font-black uppercase tracking-widest text-orange-400">Total de Itens</span>
                        <Database size={20} />
                      </div>
                      <div className="text-4xl font-black">{cachedItems.length}</div>
                      <p className="text-[10px] opacity-60 mt-2 font-bold">QUESTÕES ENGENHADAS NO ACERVO</p>
                   </div>

                   <div className="space-y-4 pt-4">
                      <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Distribuição por Disciplina</h4>
                      {Object.entries(
                        cachedItems.reduce((acc, it) => {
                          const s = it.metadata?.subj || 'Outros';
                          acc[s] = (acc[s] || 0) + 1;
                          return acc;
                        }, {})
                      ).map(([subj, count]) => (
                        <div key={subj} className="flex justify-between items-center p-3 bg-slate-50 rounded-xl">
                          <span className="text-xs font-bold text-slate-700">{subj}</span>
                          <span className="bg-orange-100 text-orange-600 px-2 py-0.5 rounded text-[10px] font-black">{count}</span>
                        </div>
                      ))}
                   </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-slate-100">
               <button 
                  onClick={() => {
                    setMaterial('');
                    setImageData(null);
                    setImageDesc('');
                    setGeneratedItem(null);
                    setActiveSidebar(null);
                  }}
                  className="w-full py-3 border border-orange-200 text-orange-600 rounded-xl text-xs font-bold hover:bg-orange-50 transition-all flex items-center justify-center gap-2"
               >
                  <Plus size={16} /> Criar Nova Do Zero
               </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionaCreator;
